import { useState } from 'react';

export type InAppBrowserPlatform =
  | 'ios'
  | 'android'
  | 'other';

export interface InAppBrowserInfo {
  isInAppBrowser: boolean;
  browserName: string | null;
  platform: InAppBrowserPlatform;
}

interface InAppBrowserPattern {
  name: string;
  tokens: string[];
}

// Test-only escape hatch so e2e specs can force the overlay branch.
declare global {
  interface Window {
    __IAB_TEST_OVERRIDE__?: InAppBrowserInfo;
  }
}

const IN_APP_PATTERNS: InAppBrowserPattern[] = [
  // Messenger UAs also contain Facebook tokens, so check Messenger first.
  {
    name: 'Messenger',
    tokens: ['Messenger', 'MessengerLite'],
  },
  {
    name: 'Facebook',
    tokens: ['FBAN', 'FBAV', 'FB_IAB', 'FBIOS'],
  },
  { name: 'Instagram', tokens: ['Instagram'] },
  { name: 'Snapchat', tokens: ['Snapchat'] },
  {
    name: 'TikTok',
    tokens: [
      'TikTok',
      'BytedanceWebview',
      'musical_ly',
      'trill',
    ],
  },
  { name: 'Twitter', tokens: ['Twitter'] },
  { name: 'LinkedIn', tokens: ['LinkedIn'] },
  { name: 'WhatsApp', tokens: ['WhatsApp'] },
  { name: 'LINE', tokens: ['Line', 'LINE'] },
  { name: 'WeChat', tokens: ['MicroMessenger'] },
  { name: 'KakaoTalk', tokens: ['KakaoTalk'] },
];

const GENERIC_WEBVIEW_TOKENS = ['; wv)', 'webview'];

function detectPlatform(
  userAgent: string
): InAppBrowserPlatform {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'other';
}

function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;

  if (
    typeof navigator !== 'undefined' &&
    'standalone' in navigator &&
    (navigator as Navigator & { standalone?: boolean })
      .standalone === true
  ) {
    return true;
  }

  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches
  ) {
    return true;
  }

  return false;
}

function isLocalStorageRestricted(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    if (typeof globalThis.localStorage !== 'object') {
      return true;
    }

    const key = `__iab_test_${Math.random()}`;
    globalThis.localStorage.setItem(key, key);
    globalThis.localStorage.removeItem(key);
    return false;
  } catch {
    return true;
  }
}

export function detectInAppBrowser(
  userAgent: string
): InAppBrowserInfo {
  const platform = detectPlatform(userAgent);

  if (isStandalonePWA()) {
    return {
      isInAppBrowser: false,
      browserName: null,
      platform,
    };
  }

  const uaLower = userAgent.toLowerCase();

  for (const pattern of IN_APP_PATTERNS) {
    for (const token of pattern.tokens) {
      if (uaLower.includes(token.toLowerCase())) {
        return {
          isInAppBrowser: true,
          browserName: pattern.name,
          platform,
        };
      }
    }
  }

  for (const token of GENERIC_WEBVIEW_TOKENS) {
    if (uaLower.includes(token.toLowerCase())) {
      return {
        isInAppBrowser: true,
        browserName: 'this app',
        platform,
      };
    }
  }

  if (isLocalStorageRestricted()) {
    return {
      isInAppBrowser: true,
      browserName: 'this app',
      platform,
    };
  }

  return {
    isInAppBrowser: false,
    browserName: null,
    platform,
  };
}

export function useInAppBrowser(): InAppBrowserInfo {
  const [info] = useState<InAppBrowserInfo>(() => {
    if (
      typeof window !== 'undefined' &&
      window.__IAB_TEST_OVERRIDE__
    ) {
      return window.__IAB_TEST_OVERRIDE__;
    }

    const userAgent =
      typeof navigator !== 'undefined'
        ? navigator.userAgent
        : '';

    return detectInAppBrowser(userAgent);
  });

  return info;
}
